import React from 'react'
import {
  ChartBarIcon,
  HeartIcon,
  ClockIcon,
  ExclamationTriangleIcon,
} from '@heroicons/react/24/outline'

interface RedditAppUsageStatsProps {
  currentUsage: number
  dailyLimit: number
  healthScore: number
  lastReset?: string
  status: string
  canMakeRequests: boolean
}

const RedditAppUsageStats: React.FC<RedditAppUsageStatsProps> = ({
  currentUsage,
  dailyLimit,
  healthScore,
  lastReset,
  status,
  canMakeRequests,
}) => {
  const usagePercentage = Math.min((currentUsage / dailyLimit) * 100, 100)
  const isNearLimit = usagePercentage >= 80
  const isCritical = usagePercentage >= 95
  const isHealthLow = healthScore < 50
  const isHealthCritical = healthScore < 20

  const getUsageColor = () => {
    if (isCritical) return 'bg-red-500'
    if (isNearLimit) return 'bg-yellow-500'
    return 'bg-blue-500'
  }

  const getHealthColor = () => {
    if (isHealthCritical) return 'text-red-600'
    if (isHealthLow) return 'text-yellow-600'
    return 'text-green-600'
  }

  const getHealthBgColor = () => {
    if (isHealthCritical) return 'bg-red-100'
    if (isHealthLow) return 'bg-yellow-100'
    return 'bg-green-100'
  }

  const getStatusBadge = () => {
    if (!canMakeRequests) {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-red-100 text-red-800">
          <ExclamationTriangleIcon className="h-3 w-3 mr-1" />
          Limited
        </span>
      )
    }
    if (status === 'active') {
      return (
        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-green-100 text-green-800">
          Active
        </span>
      )
    }
    return (
      <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-gray-100 text-gray-800">
        {status}
      </span>
    )
  }

  const formatLastReset = (resetTime?: string) => {
    if (!resetTime) return 'Never'

    const resetDate = new Date(resetTime)
    const now = new Date()
    const diffMs = now.getTime() - resetDate.getTime()
    const diffHours = Math.floor(diffMs / (1000 * 60 * 60))
    const diffMins = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60))

    if (diffHours > 0) {
      return `${diffHours}h ${diffMins}m ago`
    }
    return `${diffMins}m ago`
  }

  return (
    <div className="border rounded-lg p-6 bg-white">
      <div className="flex items-center justify-between mb-6">
        <h4 className="text-lg font-medium text-gray-900 flex items-center">
          <ChartBarIcon className="h-5 w-5 mr-2 text-blue-500" />
          Usage Statistics
        </h4>
        {getStatusBadge()}
      </div>

      <div className="space-y-6">
        {/* Daily Usage */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700">Daily API Usage</span>
            <span className="text-sm text-gray-900 font-semibold">
              {currentUsage} / {dailyLimit} requests
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${getUsageColor()}`}
              style={{ width: `${usagePercentage}%` }}
            />
          </div>
          <div className="flex items-center justify-between mt-1">
            <span className="text-xs text-gray-500">
              {(100 - usagePercentage).toFixed(1)}% remaining
            </span>
            {isNearLimit && (
              <span className="text-xs text-yellow-600 font-medium">
                {isCritical ? '⚠️ Critical - Limit almost reached!' : '⚠️ Warning - Approaching limit'}
              </span>
            )}
          </div>
        </div>

        {/* Health Score */}
        <div>
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm font-medium text-gray-700 flex items-center">
              <HeartIcon className="h-4 w-4 mr-1" />
              Health Score
            </span>
            <span className={`text-sm font-semibold ${getHealthColor()}`}>
              {healthScore}/100
            </span>
          </div>
          <div className="w-full bg-gray-200 rounded-full h-3">
            <div
              className={`h-3 rounded-full transition-all ${
                isHealthCritical ? 'bg-red-500' : isHealthLow ? 'bg-yellow-500' : 'bg-green-500'
              }`}
              style={{ width: `${healthScore}%` }}
            />
          </div>
          {isHealthCritical && (
            <div className={`mt-2 p-3 rounded-md ${getHealthBgColor()}`}>
              <p className="text-xs text-red-800">
                ⚠️ <strong>Critical Health Score!</strong> Your app is blocked from making requests.
                Health score must be above 20 to continue. Errors are causing health degradation.
              </p>
            </div>
          )}
          {isHealthLow && !isHealthCritical && (
            <div className={`mt-2 p-3 rounded-md ${getHealthBgColor()}`}>
              <p className="text-xs text-yellow-800">
                ⚠️ <strong>Low Health Score.</strong> Your app health is declining due to errors.
                Monitor your API usage and check for issues.
              </p>
            </div>
          )}
        </div>

        {/* Last Reset */}
        {lastReset && (
          <div className="flex items-center justify-between py-3 border-t">
            <span className="text-sm font-medium text-gray-700 flex items-center">
              <ClockIcon className="h-4 w-4 mr-1" />
              Last Reset
            </span>
            <span className="text-sm text-gray-600">{formatLastReset(lastReset)}</span>
          </div>
        )}

        {/* Info Message */}
        {!canMakeRequests && (
          <div className="p-4 bg-red-50 border border-red-200 rounded-md">
            <p className="text-sm text-red-800">
              <strong>Requests Blocked:</strong> Your app cannot make requests because{' '}
              {healthScore < 20
                ? 'health score is below 20'
                : currentUsage >= dailyLimit
                ? 'daily limit has been reached'
                : 'it is inactive'}
              . {healthScore < 20 ? 'Reduce errors to improve health.' : 'Wait for daily reset.'}
            </p>
          </div>
        )}

        <div className="pt-4 border-t">
          <p className="text-xs text-gray-500">
            ℹ️ Usage resets automatically every 24 hours. Health score decreases with errors and
            increases with successful requests.
          </p>
        </div>
      </div>
    </div>
  )
}

export default RedditAppUsageStats
